/**
 * Constants for Team Metrics calculations
 * These values can be tweaked independently and tests will adapt to changes
 */

// Scenario-based target configurations
export const SCENARIO_TARGETS = {
  conservative: {
    layers: { max: 10 },
    span: { min: 4, max: 8 },
    managerToICRatio: { min: 1 / 5 }, // 1:5
    managementOverhead: { max: 20 }, // 20% max overhead
    headcountReduction: { percentage: 0.05 }, // 5% reduction target
  },
  standard: {
    layers: { max: 8 },
    span: { min: 5, max: 12 },
    managerToICRatio: { min: 1 / 6 }, // 1:6
    managementOverhead: { max: 15 }, // 15% max overhead
    headcountReduction: { percentage: 0.1 }, // 10% reduction target
  },
  aggressive: {
    layers: { max: 6 },
    span: { min: 8, max: 10 },
    managerToICRatio: { min: 1 / 10 }, // 1:10
    managementOverhead: { max: 10 }, // 10% max overhead
    headcountReduction: { percentage: 0.15 }, // 15% reduction target
  },
};

// Default values (used when scenario is not provided)
export const DEFAULT_VALUES = {
  MAX_LAYERS: 8,
  SPAN_MIN: 5,
  SPAN_MAX: 12,
  MIN_MANAGER_TO_IC_RATIO: 1 / 6, // 1:6 minimum
  MANAGEMENT_OVERHEAD_MAX: 15, // 15% max overhead (fallback)
};

// RAG (Red/Amber/Green) status thresholds
export const RAG_THRESHOLDS = {
  // Span: percentage outside range before amber/red
  SPAN_RANGE_TOLERANCE: 0.2, // 20% outside range = amber

  // Ratio: percentage below target before amber/red
  RATIO_AMBER_THRESHOLD: 0.8, // 80% of target = amber threshold

  // Overhead: percentage above target before amber/red
  OVERHEAD_AMBER_MULTIPLIER: 1.2, // 20% above target = amber threshold

  // Headcount: percentage above target before amber/red
  HEADCOUNT_AMBER_MULTIPLIER: 1.1, // 10% above target = amber threshold
};

// Manager health thresholds
export const MANAGER_HEALTH_THRESHOLDS = {
  SEVERELY_OVERLOADED: 15, // Span > 15 = severely overloaded
};

// Team size distribution ranges
export const TEAM_SIZE_RANGES = {
  conservative: { min: 5, max: 20 },
  standard: { min: 5, max: 15 },
  aggressive: { min: 7, max: 12 },
};

// Team size distribution buckets
export const TEAM_SIZE_BUCKETS = {
  TOO_SMALL: { min: 1, max: 4 },
  LARGE: { max: 20 }, // Used as max for "large" bucket
  TOO_LARGE: { min: 21, max: Infinity },
};

// Issue detection thresholds
export const ISSUE_THRESHOLDS = {
  DIRECT_MEMBERS_TOO_HIGH: 20,
  DIRECT_MEMBERS_TOO_LOW: 2,
  CHILD_TEAMS_TOO_MANY: 10,
  CHILD_TEAMS_TOO_FEW: 3, // When team is large, should have at least this many
  LARGE_TEAM_THRESHOLD: 50, // Teams with more than this are considered "large"
  VERY_LARGE_TEAM_THRESHOLD: 100, // Teams with more than this are considered "very large"
  SPAN_OF_CONTROL_TOO_HIGH: 15, // Direct reports > 15 = span of control too high
};

// Helper function to get scenario targets
export const getScenarioTargets = (scenario = "standard") => {
  return SCENARIO_TARGETS[scenario] || SCENARIO_TARGETS.standard;
};

// Helper function to get team size ranges for scenario
export const getTeamSizeRanges = (scenario = "standard") => {
  return TEAM_SIZE_RANGES[scenario] || TEAM_SIZE_RANGES.standard;
};
